<template>
	<div class="block-editor-dispatcher height-100">
		<div v-if="isShowWidgetEditor && !isChangingAction">
			<WidgetBlockEditor :nodeID="nodeID" />
		</div>
		<div v-else-if="!isChangingAction && (!isAppSelected || isShowSelectApp)">
			<div class="block-editor-container">
				<perfect-scrollbar ref="scroll" class="block-editor-content-section height-100">
					<transition name="fromLeft" mode="out-in">
						<div class="app-select display-flex flex-column">
							<BlockEditorAppSelect
								class="flex-stretch-to-scroll"
								@app-selected="appSelected"
								@mini-app-selected="miniAppSelected"
								@cancel-change="cancelChangeApp"
								@action-collection-selected="actionCollectionSelected"
								@showSelectAppScreen="showSelectAppScreen"
								@action-selected="onActionSelected"
							/>
							<div @click="onAskForIntegration" class="ask-for-intigration-container display-flex flex-row flex-centerize">
								<SIcon name="ask-for-integration" class="ask-for-integration" />
								Ask for an integration or helper
							</div>
						</div>
					</transition>
				</perfect-scrollbar>
			</div>
		</div>
		<!-- // todo: DEV_HACK -->
		<div v-else-if="(isWebhook || isScheduledWebhook) && !isSubFlowInput">
			<WebhookBlockEditor :nodeID="nodeID" />
		</div>
		<div v-else-if="isSubFlow">
			<SubflowBlockEditor :nodeID="nodeID" :hasEditPermissions="hasEditPermissions" />
		</div>

		<div v-else-if="isSubFlowInput">
			<SubflowInputBlockEditor :nodeID="nodeID" :hasEditPermissions="hasEditPermissions" />
		</div>
		<div v-else-if="isSubFlowOutput">
			<SubflowOutputBlockEditor :nodeID="nodeID" />
		</div>
		<div v-else>
			<IntegrationBlockEditor
				:nodeID="nodeID"
				:hasEditPermissions="hasEditPermissions"
				:selectedServiceActionCollectionId="selectedServiceActionCollectionId"
				@setIsChangingAction="setIsChangingAction"
				@setIsShowSelectApp="setIsShowSelectApp"
			/>
		</div>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapActions, mapGetters } from 'vuex';

/* Components */
import BlockEditorAppSelect from './BlockEditorAppSelect/BlockEditorAppSelect';
// todo: move all editors to ./BlockEditors/ folder
import IntegrationBlockEditor from './IntegrationBlockEditor';
import WebhookBlockEditor from './WebhookBlockEditor';
import WidgetBlockEditor from './WidgetBlockEditor';
import SubflowBlockEditor from './editors/SubflowBlockEditor';
import SubflowInputBlockEditor from './editors/SubflowInputBlockEditor';
import SubflowOutputBlockEditor from './editors/SubflowOutputBlockEditor';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getAction } from '@/store/modules/actions/getters';
import { getService } from '@/store/modules/apps/getters';
import { setIsChangingAction } from '@/store/modules/blockEditor/actions';
import { isChangingAction } from '@/store/modules/blockEditor/getters';
import { getAccountConnection } from '@/store/modules/credentials/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { getPlacement } from '@/store/modules/placements/getters';
import { clearEditNode, setEditNodeID } from '@/store/modules/selectedNode/actions';
import { resetWidgetFieldTestStatus } from '@/store/modules/selectedNode/actions.js';
import {
	editNode,
	nodeType,
	isWidget,
	isEndRunWidget,
	isWebhook,
	isScheduledWebhook,
	isSubFlow,
	isEndRun,
	isSendNotification,
	notification,
	isTriggerNode,
} from '@/store/modules/selectedNode/getters';
import {
	editWorkflow,
	rootNode,
	actionContextNode,
	element,
	editedWorkflowVersion,
	elementContextualData,
} from '@/store/modules/selectedWorkflow/getters';
import { removeTempBlock } from '@/store/modules/treeBlocks/actions';
import { widget } from '@/store/modules/widgetEditor/getters';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { fetchIsWaitingForSampleData as getWorkflowWaitingForSampleData } from '@/store/modules/workflows/actions';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import ErrorHandlingService, { NodeCreationError, NodeUpdateError } from '@/utils/error-handling-service';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import { WAITING_FLAG_TYPES } from '@/utils/workflow-service.const';

export default {
	name: 'BlockEditorDispatcher',
	components: {
		WidgetBlockEditor,
		IntegrationBlockEditor,
		BlockEditorAppSelect,
		WebhookBlockEditor,
		SubflowBlockEditor,
		SubflowInputBlockEditor,
		SubflowOutputBlockEditor,
	},
	props: {
		nodeID: { type: [Number, String] },
	},
	data() {
		return {
			isShowParams: null,
			isShowSelectApp: false,
			isShowTestResultModal: false,
			isShowAnimation: false,
			isWaitingForSampleData: false,
			selectedServiceActionCollectionId: null,
			timer: null,
		};
	},
	async mounted() {
		await this[setEditNodeID.fullPath]({ id: this.nodeID });
		this.initShowParams();
	},
	watch: {
		appInfo() {
			this.initShowParams(); // whenever appInfo changes, meaning we just finished loaded service info, we should execute initShowParams
		},
	},
	computed: {
		...mapGetters([
			editNode.fullPath,
			nodeType.fullPath,
			widget.fullPath,
			getService.fullPath,
			rootNode.fullPath,
			actionContextNode.fullPath,
			element.fullPath,
			editedWorkflowVersion.fullPath,
			elementContextualData.fullPath,
			getAction.fullPath,
			getAccountConnection.fullPath,
			getConfigurationFieldResponse.fullPath,
			isConfigurationsOn.fullPath,
			editWorkflow.fullPath,
		]),
		...mapGettersShorthand({ isWidget, isEndRunWidget, isSendNotification, notification, isTriggerNode, isWebhook, isScheduledWebhook }),
		...mapGettersShorthand({
			canEditWorkflow,
			isWidget,
			isEndRunWidget,
			isSendNotification,
			notification,
			isTriggerNode,
			isWebhook,
			isScheduledWebhook,
			isSubFlow,
			isEndRun,
			getPlacement,
		}),
		...mapGettersShorthand({ isChangingAction }),
		workflow() {
			return this[editWorkflow.fullPath];
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.workflow.id);
		},
		isShowWidgetEditor() {
			return this.isWidget || this.isEndRunWidget;
		},
		isSubFlowInput() {
			return WorkflowService.isOfTypeSubFlow(this.workflow) && this.isWebhook;
		},
		isSubFlowOutput() {
			return WorkflowService.isOfTypeSubFlow(this.workflow) && this.isEndRun;
		},
		triggerConfig() {
			return this.workflow.trigger_config;
		},
		isSuccessResultStatus() {
			return this.testResultStatus === TEST_RESULT_STATUSES.SUCCESS;
		},
		currentlySelectedAction() {
			return this[editNode.fullPath].action ? this[getAction.fullPath](this[editNode.fullPath].action) : null;
		},
		skipAuthAction() {
			return this.currentlySelectedAction?.skip_auth;
		},
		isAppSelected() {
			return !!this[editNode.fullPath].service || !!this.selectedServiceActionCollectionId;
		},
		relevantEditNode() {
			if (this.isWebhook && this.hasContextualDataFromElement) {
				// In case on context extracted without contextNode - only for isWebhook nodes.
				return this[rootNode.fullPath];
			}
			return this[editNode.fullPath];
		},
		actionContextNode() {
			return this[actionContextNode.fullPath];
		},
		elementContextualData() {
			return this[elementContextualData.fullPath] || {};
		},
		hasContextualDataFromElement() {
			// Don't remove the check for isWebhook ! so we know we are in the rootNode of a contextual action
			// Check that there isn't actionContextNode, and there is elementContextualData data
			return this.isWebhook && !this.actionContextNode && Object.keys(this.elementContextualData).length > 0;
		},
		appInfo() {
			if (this.hasContextualDataFromElement) {
				return this[getService.fullPath](this.getPlacement(this[element.fullPath].placement).service);
			}
			return this[getService.fullPath](this.relevantEditNode?.service);
		},
		serviceVersion() {
			return this.relevantEditNode?.service_version;
		},
		configurationApproved() {
			return this[editNode.fullPath].configuration_approved;
		},
	},
	methods: {
		...mapActions([resetWidgetFieldTestStatus.fullPath, setEditNodeID.fullPath]),
		...mapActionsShorthand({
			setIsChangingAction,
			getWorkflowWaitingForSampleData,
			removeTempBlock,
			clearEditNode,
		}),
		/* Automation sample data state */
		async getSampleDataState() {
			this.isWaitingForSampleData = await this.getWorkflowWaitingForSampleData({
				id: this[editWorkflow.fullPath].id,
				waiting_type: WAITING_FLAG_TYPES.AUTOMATION_SAMPLE,
			});
			if (this.isWaitingForSampleData) {
				this.startWaitingSampleDataTimer();
			}
		},
		startWaitingSampleDataTimer() {
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				this.isWaitingForSampleData = false;
			}, 1000 * 60 * 3);
		},
		resetWidgetFieldTestStatusDebounced: debounce(function (id) {
			this[resetWidgetFieldTestStatus.fullPath](id);
		}, 500),
		onAskForIntegration() {
			this.openAskForIntegrationDialog();
		},
		hideTestCard() {
			this.isShowTestResultModal = false;
		},
		setIsShowParams(newV) {
			if (!newV && this.isShowParams == null) {
				setTimeout(() => {
					this.isShowAnimation = true;
				}, 500);
			}
			this.isShowParams = newV;
		},
		async onFailure(err) {
			this.$toast.error(ErrorHandlingService.extractErrorMessage(err)); // selecting action from search
			if (err instanceof NodeCreationError) {
				await this.removeTempBlock();
				await this.clearEditNode();
			} else if (!(err instanceof NodeUpdateError)) {
				console.error(err);
			}
		},
		async onActionSelected(newAction) {
			const isNoActionSelected = !this.currentlySelectedAction;
			if (isNoActionSelected || this.currentlySelectedAction.id !== newAction.id) {
				await WorkflowNodeService.partialUpdateEditNode(
					{ service: newAction.service_id, action: newAction.id },
					{ onFailure: this.onFailure }
				);
			}
			this.afterAppSelected();
		},
		async appSelected(app) {
			await WorkflowNodeService.partialUpdateEditNode({ service: app.id }, { onFailure: this.onFailure });
			this.afterAppSelected();
		},
		afterAppSelected() {
			const scroll = this.$refs.scroll;
			if (scroll) {
				scroll.$el.scrollTop = 0;
			}
			this.isShowSelectApp = false;
			this.selectedServiceActionCollectionId = null;
		},
		async miniAppSelected(app) {
			await WorkflowNodeService.partialUpdateEditNode({
				action_slug: app.action_slug,
				params_map: { mini_app_type: app.miniAppType },
				service: app.id,
			});
			this.afterAppSelected();
		},
		actionCollectionSelected(serviceActionCollectionId) {
			this.$refs.scroll.$el.scrollTop = 0;
			this.selectedServiceActionCollectionId = serviceActionCollectionId;
			this.isShowSelectApp = false;
		},
		cancelChangeApp() {
			this.isShowSelectApp = false;
			this.initShowParams();
		},
		async openAskForIntegrationDialog() {
			await AppDialogManagerService.openIntegrationDialog();
		},
		initShowParams() {
			if (this.isTriggerNode && this.triggerConfig?.subscribed) {
				this.getSampleDataState();
				this.setIsShowParams(true);
			} else if (this.isSendNotification) {
				this.setIsShowParams(true);
			} else {
				const nodeHasValidCredentials = WorkflowNodeService.checkIfCredentialsAreValidForNode(
					this[editedWorkflowVersion.fullPath],
					this.appInfo,
					this[editNode.fullPath],
					this.skipAuthAction,
					this[getAccountConnection.fullPath],
					this[getConfigurationFieldResponse.fullPath],
					this[isConfigurationsOn.fullPath]
				);
				this.isShowParams = this.configurationApproved && nodeHasValidCredentials && !this.isShowWidgetEditor;
			}
		},
		showSelectAppScreen() {
			this.setIsShowParams(false);
			this.isShowSelectApp = true;
		},
		setIsShowSelectApp(bool) {
			this.isShowSelectApp = bool;
			if (bool) {
				this.setIsChangingAction(false);
			}
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/components/WidgetForm/widget-form-mixins.scss';

$overlay-z-index: 50;
.block-editor-dispatcher {
	> div {
		//children require that their parent will have a set height (e.g. 100%)
		// in order to be able to set their height to 100% so that they can scroll
		height: 100%;
	}

	.block-editor-container {
		$ask-for-intigration-container-height: 50px;

		height: 100%;
		overflow: hidden;

		.app-select {
			position: absolute;
			left: 0;
			top: 0;
			height: 100%;
			width: 100%;
		}

		.ask-for-integration {
			margin: 8px;
		}

		.ask-for-intigration-container {
			height: $ask-for-intigration-container-height;
			width: 100%;
			font-size: 14px;
			color: var(--primary-color);
			border-top: 1px solid var(--border-popover);
			background-color: var(--ask-for-ingreation-background);

			&:hover {
				background-color: var(--ask-for-ingreation-hover-background);
				cursor: pointer;
			}
		}
	}
}
</style>
